import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';

const quizQuestions = [
  {
    question: "What is occupational wellness?",
    options: [
      "Having a high-paying job",
      "Finding purpose, satisfaction, and balance in daily work",
      "Working overtime every week",
      "Never taking breaks"
    ],
    correctAnswer: 1,
    explanation: "Occupational wellness is about finding purpose, satisfaction, and balance in your daily work, not just having a job or working long hours."
  },
  {
    question: "Why is awareness important for occupational wellness?",
    options: [
      "It helps you ignore problems",
      "It is the first step to improvement",
      "It makes work harder",
      "It only matters for managers"
    ],
    correctAnswer: 1,
    explanation: "Awareness is the first step – you can't improve what you don't recognize."
  },
  {
    question: "Which of the following best describes meaningful work?",
    options: [
      "Work that aligns with your values and allows time to rest, grow, and connect",
      "Work that is always stressful",
      "Work with no breaks",
      "Work that only pays well"
    ],
    correctAnswer: 0,
    explanation: "Meaningful work aligns with your values and allows time to rest, grow, and connect."
  },
  {
    question: "What is one way to improve your occupational wellness?",
    options: [
      "Ignore your needs",
      "Set goals and seek feedback",
      "Work without breaks",
      "Avoid learning new skills"
    ],
    correctAnswer: 1,
    explanation: "Setting goals and seeking feedback helps you grow and improve your occupational wellness."
  },
  {
    question: "Occupational wellness can help you:",
    options: [
      "Feel more satisfied and balanced in life",
      "Become more stressed",
      "Work longer hours",
      "Ignore your values"
    ],
    correctAnswer: 0,
    explanation: "Occupational wellness helps you feel more satisfied and balanced in life."
  }
];

const OccupationalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { saveProgress } = useModuleProgress('occupationalM1');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (index: number) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(index);
      setShowExplanation(true);
    }
  };

  const handleNext = async () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
  await saveProgress(quizQuestions.length, true); // Mark module as completed
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">You've completed the Module 1 quiz!</p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <ul className="achievement-list">
              <li><span className="check"></span> Awareness of occupational wellness</li>
              <li><span className="check"></span> Understanding meaningful work</li>
              <li><span className="check"></span> Recognizing the value of balance</li>
            </ul>
          </div>
          <button className="resource-button" onClick={() => navigate('/panel/learn/occupational-resources')}>Return to Resources</button>
        </div>
      </div>
    );
  }

  const currentQuiz = quizQuestions[currentQuestion];
  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left occupational-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Foundations of Occupational Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">Test your understanding of occupational wellness and meaningful work.</p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}></div>
              </div>
              <span className="progress-text">Question {currentQuestion + 1} of {quizQuestions.length}</span>
            </div>
            <div className="question-section">
              <h2 className="question-title">{currentQuiz.question}</h2>
              <div className="answer-options">
                {currentQuiz.options.map((option, idx) => (
                  <label key={idx} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={idx}
                      checked={selectedAnswer === idx}
                      onChange={() => handleAnswerSelect(idx)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button className="btn-secondary" onClick={currentQuestion === 0 ? () => navigate('/panel/learn/occupational-resources') : handleBack}>
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Next Question</button>
              ) : (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Complete Quiz</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule1QuizPage;


